// Screen frame parts (mockup ui.screen / ui.pageHead / ui.panel / ui.note / ui.crumbs).
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useFmt, useLang, useT } from '../i18n';
import { cls } from '../lib/util';
import { Icon, type IconName } from './Icon';
import { TermScope } from './Term';

/** Screen root: <section class="scr scr-<name>"> — screen CSS selectors live under .scr-<name>. One h1 per screen (PageHead). */
export function Screen({ name, children, className }: { name: string; children: ReactNode; className?: string }) {
  return (
    <section className={cls('scr', `scr-${name}`, className)}>
      <TermScope kind="screen">{children}</TermScope>
    </section>
  );
}

export interface PageHeadProps {
  title: ReactNode;
  kicker?: ReactNode;
  /** Lead sentence under the title. */
  desc?: ReactNode;
  /** "이 화면이 답하는 질문" line (plan 8.5.1). */
  question?: ReactNode;
  actions?: ReactNode;
}
export function PageHead({ title, kicker, desc, question, actions }: PageHeadProps) {
  const lang = useLang();
  return (
    <header className="page-head">
      <div className="page-head__text">
        {kicker ? <p className="eyebrow">{kicker}</p> : null}
        <h1 className="page-head__title" tabIndex={-1}>
          {title}
        </h1>
        {desc ? <p className="page-head__lead">{desc}</p> : null}
        {question ? (
          <p className="page-head__q">
            <Icon name="help" />
            <span>
              <span className="visually-hidden">{lang === 'en' ? 'This page answers: ' : '이 화면이 답하는 질문: '}</span>
              {question}
            </span>
          </p>
        ) : null}
      </div>
      {actions ? <div className="page-head__actions">{actions}</div> : null}
    </header>
  );
}

/** Records/people left out of a total: { records, people, byReason: { reason: n }, deleted } (aggregate().excluded). */
export interface Excluded {
  records?: number;
  people?: number;
  byReason?: Readonly<Record<string, number>>;
  deleted?: number;
}
/** '제외 3명·52건(기록 수준에서 꺼짐, …) · 삭제 요청으로 2건 제외' — App.excludedText. */
export function useExcludedText() {
  const t = useT();
  const lang = useLang();
  return (ex: Excluded | null | undefined): string | null => {
    if (!ex || (!ex.records && !ex.deleted)) return null;
    const out: string[] = [];
    if (ex.records) {
      const by = ex.byReason ?? {};
      const reasons = Object.keys(by)
        .sort((a, b) => (by[b] ?? 0) - (by[a] ?? 0))
        .map((r) => t('reason.' + r));
      const base = ex.people ? t('card.excludedPeople', { p: ex.people, n: ex.records }) : t('card.excluded', { n: ex.records });
      out.push(base + (reasons.length ? (lang === 'en' ? ' (' : '(') + reasons.slice(0, 2).join(', ') + ')' : ''));
    }
    if (ex.deleted) out.push(t('card.excludedDeletion', { n: ex.deleted }));
    return out.join(' · ');
  };
}

export interface PanelProps {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  /** Custom footer, or false for none. Without it, `count`/`source` build the standard footer. */
  footer?: ReactNode | false;
  count?: number;
  source?: string;
  excluded?: Excluded;
  updatedAt?: number;
  wide?: boolean;
  id?: string;
  className?: string;
}
export function Panel({ title, sub, actions, children, footer, count, source, excluded, updatedAt, wide, id, className }: PanelProps) {
  const t = useT();
  const fmt = useFmt();
  const exText = useExcludedText();
  const autoId = useId();
  const pid = id ?? `panel${autoId.replace(/[^a-zA-Z0-9]/g, '')}`;
  let foot: ReactNode = null;
  if (footer !== false) {
    if (footer != null) foot = <div className="panel__foot">{footer}</div>;
    else if (count != null || source)
      foot = (
        <div className="panel__foot">
          <span className="status-dot status-dot--online" aria-hidden="true" />
          <span>
            {[source, count != null ? t('common.basis', { n: count }) : null, exText(excluded), t('common.updated', { time: fmt.time(updatedAt ?? Date.now()) }), t('card.noRecord')]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      );
  }
  return (
    <section className={cls('panel', wide && 'is-wide', className)} id={pid} aria-labelledby={title ? `${pid}-t` : undefined}>
      <TermScope>
        {title || actions ? (
          <div className="panel__head">
            <div className="panel__heading">
              <h2 className="panel__title" id={`${pid}-t`}>
                {title}
              </h2>
              {sub ? <p className="panel__sub">{sub}</p> : null}
            </div>
            {actions ? <div className="panel__tools">{actions}</div> : null}
          </div>
        ) : null}
        <div className="panel__body">{children}</div>
        {foot}
      </TermScope>
    </section>
  );
}

/** Source / basis sentence under a number (ui.note). */
export function Note({ children, icon = 'info' }: { children: ReactNode; icon?: IconName }) {
  return (
    <p className="src-note">
      <Icon name={icon} />
      <span>{children}</span>
    </p>
  );
}

/** Breadcrumb (103): the last item is the current page. */
export function Crumbs({ items }: { items: ReadonlyArray<{ label: ReactNode; to?: string }> }) {
  const lang = useLang();
  return (
    <nav className="crumbs" aria-label={lang === 'en' ? 'Breadcrumb' : '현재 위치'}>
      <ol>
        {items.map((it, i) => {
          const last = i === items.length - 1;
          return (
            <li key={i}>
              {last || !it.to ? <span aria-current={last ? 'page' : undefined}>{it.label}</span> : <Link to={it.to}>{it.label}</Link>}
              {last ? null : <Icon name="chevR" className="crumbs__sep" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
